import { isEqual } from 'es-toolkit';
import React, { useMemo, useState } from 'react';
import { Container, Divider, Grid, Paper } from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { HangerList } from '@paragon/contract-library';
import { hasMaterialsEditPermission } from '@paragon/login-tools';
import { getDefaultHangerListGuid } from '@paragon/material-lists';

import { setDefaultHangerListGuid } from '../../actions/defaultMaterialSelectionActions';
import {
  archiveHangerList,
  duplicateHangerList,
  renameHangerList,
  restoreHangerList,
  upsertHangerListPrototype,
} from '../../actions/hangerListActions';
import { useHangerList } from '../../selectors/hangerListSelectors';
import { UserVisibleError } from '../../UserVisibleError';
import { GRID_SPACING } from '../../util/constants';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ListActions } from '../interface/controls/ListActions';
import { ListForm } from '../interface/forms/ListForm';
import { HangerListTable } from '../interface/HangerListTable';
import { NavigationBreadcrumbsBase } from '../interface/navigation/NavigationBreadcrumbsBase';
import { fieldsSx } from '../interface/styles';
import { LoadingPage } from './LoadingPage';

export function HangerListPage() {
  const defaultHangerListGuid = useAppSelector(getDefaultHangerListGuid);

  const hangerListFromState = useHangerList(useParams().hangerListGuid);

  if (!hangerListFromState) throw new UserVisibleError('Hanger list could not be found.');
  if (hangerListFromState === 'loading') return <LoadingPage />;

  return (
    <UnconnectedHangerListPage
      defaultHangerListGuid={defaultHangerListGuid}
      hangerListFromState={hangerListFromState}
    />
  );
}

function UnconnectedHangerListPage(props: {
  readonly defaultHangerListGuid: string | null;
  readonly hangerListFromState: HangerList;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hasEditPermission = useAppSelector(hasMaterialsEditPermission);

  const [editedHangerList, setEditedHangerList] = useState<HangerList | null>(null);
  const hangerList = editedHangerList ?? props.hangerListFromState;

  const crumbs = useMemo(
    (): [string, string][] => [
      ['Home', '/'],
      ['Hanger Lists', '/'],
      [hangerList?.name ?? '', `/hangerLists/${hangerList?.guid}`],
    ],
    [hangerList?.guid, hangerList?.name],
  );

  const changesHaveBeenMade = !isEqual(
    hangerList.hangerHardware.toSorted(),
    props.hangerListFromState.hangerHardware.toSorted(),
  );

  return (
    <Container>
      <NavigationBreadcrumbsBase crumbs={crumbs} />

      <ListActions
        type="hanger"
        list={hangerList}
        defaultListGuid={props.defaultHangerListGuid}
        setDefaultListGuid={setDefaultHangerListGuid}
        archiveList={archiveHangerList}
        restoreList={restoreHangerList}
        duplicateList={duplicateHangerList}
        renameList={renameHangerList}
      />

      <Divider sx={{ marginBottom: 2 }} />
      <Grid container spacing={GRID_SPACING}>
        <Grid sx={{ width: '100%' }} size={{ xs: 12, md: 4 }}>
          {hasEditPermission && (
            <ListForm
              pluralNoun="hangers"
              list={hangerList}
              defaultListGuid={props.defaultHangerListGuid}
              changesHaveBeenMade={changesHaveBeenMade}
              disableSubmission={hangerList.hangerHardware.length === 0}
              onChange={(changes) => {
                setEditedHangerList((old) => ({ ...(old ?? hangerList), ...changes }));
              }}
              onSubmit={async () => {
                await dispatch(upsertHangerListPrototype(hangerList));
              }}
              onSetAsDefaultList={async () => {
                await dispatch(setDefaultHangerListGuid(hangerList.guid));
              }}
              navigateAfterSubmit={() => void navigate('/')}
            />
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 8 }} sx={fieldsSx}>
          <Paper elevation={0}>
            <HangerListTable hangerList={hangerList} setEditedHangerList={setEditedHangerList} />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
